import React, {
  forwardRef, memo, useCallback, useEffect, useId, useLayoutEffect,
  useMemo, useRef, useState,
} from "react";
import { ChevronDown, Check, Search as SearchIcon, X } from "lucide-react";

/**
 * ChainFlow OS · PremiumSelect
 * ────────────────────────────────────────────────────────────
 * The fourth brand-level value-input primitive. Closes the
 * value-selection system: where the other three primitives handle
 * discrete (`PremiumStepper`), continuous (`PremiumRange`), and
 * low-cardinality categorical (`PremiumSegmented`) selection,
 * `PremiumSelect` handles **high-cardinality** categorical selection
 * — buyer / seller pickers, agent assignment, office assignment,
 * deal stage, transaction status, property type, solicitor / broker,
 * document category, workflow template, etc.
 *
 * Locked v1 interaction grammar:
 *  • Click / tap trigger to open. Pointer Events; `touch-action:
 *    manipulation` on the trigger so mobile taps land first time.
 *  • Click outside (or press Esc) to close. Tab closes and yields focus.
 *  • Keyboard (trigger focused, closed):
 *       ↓ / ↑ / Enter / Space / Alt+↓ : open
 *       (printable keys) : open and type-ahead jump
 *  • Keyboard (listbox open):
 *       ↓ : highlight next      ↑ : highlight prev
 *       Home : first option     End : last option
 *       Enter / Space : select highlighted option, close
 *       Esc / Tab     : close   Alt+↑ : close
 *       (printable keys) : type-ahead jumps (debounced 600 ms)
 *  • Search (optional, `searchable`): filters the option list and
 *    receives focus when the panel opens. Highlighted option is
 *    always the first match.
 *  • ARIA combobox 1.2 pattern: role="combobox" on the trigger,
 *    role="listbox" on the panel, role="option" + aria-selected on
 *    each option, aria-expanded, aria-controls, aria-activedescendant.
 *
 * Locked v1 visual identity:
 *  • Trigger: soft white surface, slate-200 border, violet
 *    focus-visible ring, chevron icon rotates 180° when open.
 *  • Panel: white card, slate-200 border, calm 180 ms cubic-bezier
 *    fade + 4 px translateY entrance, soft shadow with subtle
 *    violet tint, max-height 280 px with internal scroll.
 *  • Options: slate-900 text, slate-50 hover, slate-100 active
 *    highlight (keyboard cursor), selected option carries an indigo
 *    check icon on the right and a faint slate-50 wash.
 *  • Optional search input at top of panel (slate-200 bottom border).
 *  • Optional label / hint / error block above the trigger — error
 *    state turns border + ring red.
 *  • `prefers-reduced-motion` suppresses fade-in and chevron rotation.
 *
 * @prop {string|number|null} value
 * @prop {(v) => void} onChange
 * @prop {Array<{value, label, hint?, disabled?}>} options
 * @prop {string} [placeholder="Select…"]
 * @prop {string} [label]
 * @prop {string} [hint]            Calm helper line under the trigger.
 * @prop {string} [error]           Inline error message (turns ring red).
 * @prop {boolean} [searchable=false]
 * @prop {string} [searchPlaceholder="Search…"]
 * @prop {"sm"|"md"|"lg"} [size="md"]
 * @prop {boolean} [disabled=false]
 * @prop {boolean} [clearable=false]  Show an X to clear when value is set.
 * @prop {(opt) => React.Node} [renderOption]
 *   Custom row renderer; receives the option object.
 * @prop {(opt) => React.Node} [renderSelected]
 *   Custom selected-text renderer.
 * @prop {string} [emptyText="No matches"]
 * @prop {string} [ariaLabel]
 * @prop {string} [testid]
 *   Yields `testid` (trigger), `testid-panel`, `testid-search`,
 *   `testid-option-<value>`, `testid-clear`.
 * @prop {string} [className]
 */
const PremiumSelectInner = forwardRef(function PremiumSelectInner({
  value,
  onChange,
  options,
  placeholder = "Select…",
  label,
  hint,
  error,
  searchable = false,
  searchPlaceholder = "Search…",
  size = "md",
  disabled = false,
  clearable = false,
  renderOption,
  renderSelected,
  emptyText = "No matches",
  ariaLabel,
  testid,
  className = "",
}, externalRef) {
  const triggerRef = useRef(null);
  const panelRef = useRef(null);
  const searchRef = useRef(null);
  const optionRefs = useRef({});
  const typeaheadRef = useRef({ buffer: "", timer: null });

  const triggerId = useId();
  const listboxId = useId();
  const labelId = useId();
  const hintId = useId();
  const errorId = useId();

  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [highlight, setHighlight] = useState(0);
  const [panelStyle, setPanelStyle] = useState({});

  // Allow parent to access the trigger button.
  useEffect(() => {
    if (typeof externalRef === "function") externalRef(triggerRef.current);
    else if (externalRef) externalRef.current = triggerRef.current;
  }, [externalRef]);

  // ── Filtered options ──
  const filteredOptions = useMemo(() => {
    if (!searchable || !query) return options;
    const q = query.toLowerCase().trim();
    return options.filter((o) => {
      const lbl = String(o.label ?? "").toLowerCase();
      const hnt = String(o.hint ?? "").toLowerCase();
      return lbl.includes(q) || hnt.includes(q);
    });
  }, [options, searchable, query]);

  const selectedOption = useMemo(
    () => options.find((o) => o.value === value) || null,
    [options, value]
  );

  // ── Open / close ──
  const openPanel = useCallback((focusSearch = true) => {
    if (disabled) return;
    setOpen(true);
    setQuery("");
    // Highlight selected if any, else first non-disabled.
    const idx = options.findIndex((o) => o.value === value && !o.disabled);
    setHighlight(idx >= 0 ? idx : options.findIndex((o) => !o.disabled));
    // Defer focus so the panel mounts first.
    if (focusSearch && searchable) {
      setTimeout(() => searchRef.current?.focus(), 30);
    }
  }, [disabled, options, value, searchable]);

  const closePanel = useCallback((restoreFocus = true) => {
    setOpen(false);
    setQuery("");
    if (restoreFocus) {
      // Defer to next frame so the click that triggered close completes.
      setTimeout(() => triggerRef.current?.focus(), 0);
    }
  }, []);

  const selectOption = useCallback((opt) => {
    if (!opt || opt.disabled) return;
    onChange(opt.value);
    closePanel(true);
  }, [onChange, closePanel]);

  // ── Click outside ──
  useEffect(() => {
    if (!open) return;
    const onDocPointer = (e) => {
      if (
        triggerRef.current?.contains(e.target) ||
        panelRef.current?.contains(e.target)
      ) return;
      closePanel(false);
    };
    document.addEventListener("pointerdown", onDocPointer, true);
    return () => document.removeEventListener("pointerdown", onDocPointer, true);
  }, [open, closePanel]);

  // ── Reposition panel (above vs below; clamp to viewport) ──
  useLayoutEffect(() => {
    if (!open) return;
    const recalc = () => {
      const t = triggerRef.current;
      if (!t) return;
      const r = t.getBoundingClientRect();
      const vh = window.innerHeight;
      const below = vh - r.bottom;
      const above = r.top;
      const placeAbove = below < 280 && above > below;
      const maxH = Math.max(180, Math.min(280, (placeAbove ? above : below) - 16));
      setPanelStyle({
        width: r.width,
        maxHeight: maxH,
        [placeAbove ? "bottom" : "top"]: "calc(100% + 6px)",
      });
    };
    recalc();
    window.addEventListener("resize", recalc);
    window.addEventListener("scroll", recalc, true);
    return () => {
      window.removeEventListener("resize", recalc);
      window.removeEventListener("scroll", recalc, true);
    };
  }, [open]);

  // ── Scroll highlighted option into view ──
  useEffect(() => {
    if (!open) return;
    const opt = filteredOptions[highlight];
    if (!opt) return;
    const node = optionRefs.current[opt.value];
    if (node && node.scrollIntoView) {
      node.scrollIntoView({ block: "nearest" });
    }
  }, [highlight, open, filteredOptions]);

  // ── Type-ahead ──
  const typeaheadJump = useCallback((char) => {
    const t = typeaheadRef.current;
    t.buffer = (t.buffer + char).toLowerCase();
    if (t.timer) clearTimeout(t.timer);
    t.timer = setTimeout(() => { t.buffer = ""; }, 600);
    const idx = filteredOptions.findIndex(
      (o) => !o.disabled && String(o.label ?? "").toLowerCase().startsWith(t.buffer)
    );
    if (idx >= 0) setHighlight(idx);
  }, [filteredOptions]);

  // ── Keyboard on trigger / listbox ──
  const moveHighlight = useCallback((dir) => {
    if (filteredOptions.length === 0) return;
    let i = highlight;
    for (let k = 0; k < filteredOptions.length; k++) {
      i = (i + dir + filteredOptions.length) % filteredOptions.length;
      if (!filteredOptions[i]?.disabled) break;
    }
    setHighlight(i);
  }, [filteredOptions, highlight]);

  const onTriggerKeyDown = useCallback((e) => {
    if (disabled) return;
    if (!open) {
      if (["ArrowDown", "ArrowUp", "Enter", " "].includes(e.key) ||
          (e.altKey && e.key === "ArrowDown")) {
        e.preventDefault();
        openPanel(searchable);
      } else if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
        openPanel(false);
        typeaheadJump(e.key);
      }
      return;
    }
    // open
    switch (e.key) {
      case "Escape":    e.preventDefault(); closePanel(true); break;
      case "Tab":       closePanel(false); break;
      case "ArrowDown": e.preventDefault(); moveHighlight(+1); break;
      case "ArrowUp":   e.preventDefault();
        if (e.altKey) { closePanel(true); break; }
        moveHighlight(-1); break;
      case "Home":      e.preventDefault();
        setHighlight(filteredOptions.findIndex((o) => !o.disabled));
        break;
      case "End":       e.preventDefault(); {
        for (let i = filteredOptions.length - 1; i >= 0; i--) {
          if (!filteredOptions[i].disabled) { setHighlight(i); break; }
        }
        break;
      }
      case "Enter":
      case " ":         e.preventDefault(); selectOption(filteredOptions[highlight]); break;
      default:
        if (!searchable && e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
          typeaheadJump(e.key);
        }
    }
  }, [disabled, open, openPanel, searchable, typeaheadJump, closePanel,
      moveHighlight, filteredOptions, highlight, selectOption]);

  const onSearchKeyDown = useCallback((e) => {
    if (["ArrowDown", "ArrowUp", "Enter", "Escape", "Home", "End", "Tab"].includes(e.key)) {
      onTriggerKeyDown(e);
    }
  }, [onTriggerKeyDown]);

  // ── Sizing ──
  const sizing = {
    sm: { trigger: "h-8 px-3 text-[12.5px]",  panel: "text-[12.5px]" },
    md: { trigger: "h-10 px-3.5 text-[13.5px]", panel: "text-[13px]"   },
    lg: { trigger: "h-11 px-4 text-[14px]",   panel: "text-[13.5px]" },
  }[size] || { trigger: "h-10 px-3.5 text-[13.5px]", panel: "text-[13px]" };

  const hasError = !!error;
  const valuelessLabel = (
    <span className="text-slate-400">{placeholder}</span>
  );
  const selectedDisplay = selectedOption
    ? (renderSelected ? renderSelected(selectedOption) : selectedOption.label)
    : valuelessLabel;

  const activeOptionId = open && filteredOptions[highlight]
    ? `${listboxId}-${String(filteredOptions[highlight].value)}`
    : undefined;

  const onClear = (e) => {
    e.stopPropagation();
    onChange(null);
  };

  return (
    <div className={`cf-sel ${className}`} data-testid={testid ? `${testid}-wrap` : undefined}>
      {label && (
        <label
          id={labelId}
          htmlFor={triggerId}
          className="block text-[10.5px] font-semibold uppercase tracking-[0.14em] text-slate-500 mb-1.5"
        >
          {label}
        </label>
      )}
      <div className="relative">
        <button
          ref={triggerRef}
          id={triggerId}
          type="button"
          role="combobox"
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-controls={listboxId}
          aria-activedescendant={activeOptionId}
          aria-labelledby={label ? labelId : undefined}
          aria-label={!label ? ariaLabel : undefined}
          aria-describedby={hasError ? errorId : (hint ? hintId : undefined)}
          aria-invalid={hasError || undefined}
          aria-disabled={disabled || undefined}
          disabled={disabled}
          onClick={() => (open ? closePanel(false) : openPanel(searchable))}
          onKeyDown={onTriggerKeyDown}
          style={{ touchAction: "manipulation" }}
          data-testid={testid}
          className={
            `cf-sel-trigger w-full inline-flex items-center justify-between gap-2 ` +
            `${sizing.trigger} font-medium tabular-nums select-none ` +
            `rounded-xl bg-white border ` +
            (hasError
              ? "border-rose-300 focus-visible:ring-rose-300/60"
              : "border-slate-200 focus-visible:ring-violet-400/60") +
            ` focus:outline-none focus-visible:ring-2 ` +
            (disabled ? " opacity-60 cursor-not-allowed" : " cursor-pointer hover:border-slate-300")
          }
        >
          <span className="truncate text-left flex-1 text-slate-900">
            {selectedDisplay}
          </span>
          <span className="flex items-center gap-1.5 text-slate-400">
            {clearable && selectedOption && !disabled && (
              <span
                role="button"
                tabIndex={-1}
                aria-label="Clear selection"
                onPointerDown={onClear}
                data-testid={testid ? `${testid}-clear` : undefined}
                className="w-5 h-5 rounded-md hover:bg-slate-100 hover:text-slate-700 inline-flex items-center justify-center"
              >
                <X size={13} strokeWidth={2.4} />
              </span>
            )}
            <ChevronDown
              size={15}
              strokeWidth={2.4}
              className={`cf-sel-chevron transition-transform ${open ? "rotate-180" : ""}`}
            />
          </span>
        </button>
        {open && (
          <div
            ref={panelRef}
            role="listbox"
            id={listboxId}
            aria-labelledby={label ? labelId : undefined}
            data-testid={testid ? `${testid}-panel` : undefined}
            className={
              `cf-sel-panel absolute left-0 z-40 rounded-xl bg-white border border-slate-200 ` +
              `${sizing.panel} font-medium overflow-hidden flex flex-col`
            }
            style={{
              ...panelStyle,
              boxShadow:
                "0 1px 2px rgba(15,23,42,0.06), 0 12px 32px -10px rgba(79,70,229,0.22), 0 0 0 1px rgba(15,23,42,0.04)",
            }}
          >
            {searchable && (
              <div className="px-2.5 py-2 border-b border-slate-100 flex items-center gap-2">
                <SearchIcon size={13} strokeWidth={2.3} className="text-slate-400 flex-none" />
                <input
                  ref={searchRef}
                  type="text"
                  value={query}
                  onChange={(e) => { setQuery(e.target.value); setHighlight(0); }}
                  onKeyDown={onSearchKeyDown}
                  placeholder={searchPlaceholder}
                  aria-autocomplete="list"
                  aria-controls={listboxId}
                  data-testid={testid ? `${testid}-search` : undefined}
                  className="flex-1 bg-transparent outline-none text-slate-900 placeholder-slate-400 text-[12.5px]"
                />
              </div>
            )}
            <div className="flex-1 overflow-y-auto py-1" style={{ maxHeight: panelStyle.maxHeight }}>
              {filteredOptions.length === 0 ? (
                <div className="px-3 py-2 text-slate-400 text-[12.5px]">{emptyText}</div>
              ) : filteredOptions.map((opt, i) => {
                const selected = opt.value === value;
                const active = i === highlight;
                return (
                  <div
                    key={String(opt.value)}
                    ref={(el) => { optionRefs.current[opt.value] = el; }}
                    id={`${listboxId}-${String(opt.value)}`}
                    role="option"
                    aria-selected={selected}
                    aria-disabled={opt.disabled || undefined}
                    onPointerEnter={() => !opt.disabled && setHighlight(i)}
                    onClick={() => selectOption(opt)}
                    data-testid={testid ? `${testid}-option-${opt.value}` : undefined}
                    data-active={active || undefined}
                    data-selected={selected || undefined}
                    className={
                      "px-3 py-2 flex items-center gap-2 cursor-pointer " +
                      (active && !opt.disabled ? "bg-slate-100 " : "hover:bg-slate-50 ") +
                      (selected ? "text-slate-900 font-semibold " : "text-slate-700 ") +
                      (opt.disabled ? "opacity-40 cursor-not-allowed " : "")
                    }
                  >
                    <div className="flex-1 min-w-0">
                      {renderOption ? renderOption(opt) : (
                        <>
                          <div className="truncate">{opt.label}</div>
                          {opt.hint && (
                            <div className="truncate text-[11px] text-slate-500 font-normal">
                              {opt.hint}
                            </div>
                          )}
                        </>
                      )}
                    </div>
                    {selected && (
                      <Check size={14} strokeWidth={2.5} className="text-violet-600 flex-none" />
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
      {(hint && !error) && (
        <p id={hintId} className="mt-1.5 text-[11px] text-slate-500 leading-snug">
          {hint}
        </p>
      )}
      {error && (
        <p id={errorId} role="alert" className="mt-1.5 text-[11px] text-rose-600 leading-snug">
          {error}
        </p>
      )}
      <style>{`
        .cf-sel-trigger { transition: border-color 180ms ease, box-shadow 180ms ease; }
        .cf-sel-chevron { will-change: transform; }
        .cf-sel-panel {
          animation: cf-sel-in 180ms cubic-bezier(0.22, 1, 0.36, 1);
          will-change: opacity, transform;
        }
        @keyframes cf-sel-in {
          0%   { opacity: 0; transform: translateY(-4px); }
          100% { opacity: 1; transform: translateY(0); }
        }
        @media (prefers-reduced-motion: reduce) {
          .cf-sel-panel { animation: none !important; }
          .cf-sel-chevron { transition: none !important; }
        }
      `}</style>
    </div>
  );
});

PremiumSelectInner.displayName = "PremiumSelectInner";

export const PremiumSelect = memo(PremiumSelectInner);
PremiumSelect.displayName = "PremiumSelect";

export default PremiumSelect;
